/**
 * TypeScript Type Definitions
 *
 * Centralized type definitions mirroring the backend Pydantic schemas.
 * Organized by domain with ~67 types matching the backend exactly.
 */

// =============================================================================
// Base Types
// =============================================================================

/**
 * Money type - string to preserve decimal precision from backend.
 * The backend serializes Decimal values as strings to maintain accuracy.
 */
export type Money = string;

/**
 * Generic paginated response wrapper.
 * Matches backend's PaginatedResponse schema.
 */
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

// Computed pagination helpers (frontend-only convenience)
export function hasNextPage(response: PaginatedResponse<unknown>): boolean {
  const totalPages = Math.ceil(response.total / response.page_size);
  return response.page < totalPages;
}

export function hasPrevPage(response: PaginatedResponse<unknown>): boolean {
  return response.page > 1;
}

// =============================================================================
// Error Types (RFC 7807)
// =============================================================================

/**
 * RFC 7807 Problem Details for HTTP APIs.
 */
export interface ProblemDetail {
  type: string;
  title: string;
  status: number;
  detail?: string | null;
  instance?: string | null;
}

/**
 * Validation error response with field-level errors.
 */
export interface ValidationErrorDetail extends ProblemDetail {
  errors?: Array<{
    loc: (string | number)[];
    msg: string;
    type: string;
  }> | null;
}

// =============================================================================
// Literal Types (Enums)
// =============================================================================

// Expense splitting types
export type SplitType = 'personal' | 'equal' | 'custom' | 'mixed';

// Account types
export type AccountType = 'checking' | 'savings' | 'credit' | 'investment' | 'loan' | 'other';

// Transaction types
export type TransactionType = 'debit' | 'credit';

// Frequency options
export type Frequency = 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'semi_annual' | 'yearly';

// Budget period types
export type BudgetPeriod = 'weekly' | 'monthly' | 'yearly';

// Category rule matching
export type MatchType = 'contains' | 'starts_with' | 'exact' | 'regex';
export type MatchField = 'description' | 'merchant' | 'both';

// Settlement payment methods
export type SettlementMethod = 'cash' | 'venmo' | 'zelle' | 'bank_transfer' | 'other';

// Settlement types
export type SettlementType = 'full' | 'partial' | 'fresh_start';

// Bank connection status
export type BankConnectionStatus = 'active' | 'error' | 'disconnected';

// Balance history source
export type BalanceSource = 'sync' | 'manual' | 'calculated';

// Recurring transaction visibility
export type VisibilityScope = 'PRIVATE' | 'SHARED';

// Audit log actions
export type AuditAction = 'create' | 'update' | 'delete' | 'restore';

// =============================================================================
// Household Types
// =============================================================================

export interface HouseholdBase {
  name: string;
  default_split_type: SplitType;
}

export interface HouseholdCreate extends HouseholdBase {}

export interface HouseholdUpdate {
  name?: string | null;
  default_split_type?: SplitType | null;
  net_worth_sharing_enabled?: boolean | null;
}

export interface HouseholdRead extends HouseholdBase {
  id: string;
  net_worth_sharing_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface HouseholdWithMembers extends HouseholdRead {
  member_count: number;
}

// =============================================================================
// User Types
// =============================================================================

export interface UserBase {
  email: string;
  name: string;
  avatar_color?: string | null;
  default_split_percent: Money;
}

export interface UserCreate extends UserBase {
  household_id: string;
}

export interface UserUpdate {
  name?: string | null;
  avatar_color?: string | null;
  default_split_percent?: Money | null;
  is_active?: boolean | null;
  split_start_date?: string | null;
  share_net_worth?: boolean | null;
}

export interface UserRead extends UserBase {
  id: string;
  household_id: string;
  role: 'owner' | 'member';
  is_active: boolean;
  avatar_url?: string | null;
  split_start_date?: string | null;
  share_net_worth: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserSummary {
  id: string;
  name: string;
  avatar_color?: string | null;
}

// =============================================================================
// Account Types
// =============================================================================

export interface AccountBase {
  name: string;
  type: AccountType;
  subtype?: string | null;
  is_hidden: boolean;
  is_tracking_enabled: boolean;
}

export interface AccountCreate extends AccountBase {
  current_balance: Money;
}

export interface AccountUpdate {
  name?: string | null;
  is_hidden?: boolean | null;
  is_tracking_enabled?: boolean | null;
  current_balance?: Money | null;
  type?: AccountType | null;
  subtype?: string | null;
  institution_name?: string | null;
  credit_limit?: Money | null;
}

export interface AccountRead extends AccountBase {
  id: string;
  user_id: string;
  bank_connection_id: string;
  provider_account_id: string;
  official_name?: string | null;
  mask?: string | null;
  current_balance?: Money | null;
  available_balance?: Money | null;
  credit_limit?: Money | null;
  created_at: string;
  updated_at: string;
  is_system_account: boolean;
  is_manual: boolean;
}

export interface AccountSummary {
  id: string;
  name: string;
  type: string;
  mask?: string | null;
  current_balance?: Money | null;
}

export interface AccountWithBalance extends AccountRead {
  net_balance: Money;
}

export interface ManualAccountCreate {
  name: string;
  type: AccountType;
  subtype?: string | null;
  current_balance: Money;
  institution_name?: string | null;
  credit_limit?: Money | null;
}

// =============================================================================
// Category Types
// =============================================================================

export interface CategoryBase {
  name: string;
  icon?: string | null;
  color?: string | null;
  is_income: boolean;
  default_split_type: SplitType;
  sort_order: number;
}

export interface CategoryCreate extends CategoryBase {
  parent_id?: string | null;
}

export interface CategoryUpdate {
  name?: string | null;
  icon?: string | null;
  color?: string | null;
  parent_id?: string | null;
  default_split_type?: SplitType | null;
  sort_order?: number | null;
}

export interface CategoryRead extends CategoryBase {
  id: string;
  household_id: string;
  parent_id?: string | null;
  is_system: boolean;
  created_at: string;
  updated_at: string;
}

export interface CategoryTree extends CategoryRead {
  children: CategoryTree[];
}

export interface CategoryUsage {
  category_id: string;
  transaction_count: number;
}

export interface CategorySummary {
  id: string;
  name: string;
  icon?: string | null;
  color?: string | null;
}

// =============================================================================
// Transaction Types
// =============================================================================

export interface TransactionBase {
  description: string;
  amount: Money;
  transaction_type: TransactionType;
  date: string;
  merchant_name?: string | null;
  notes?: string | null;
  excluded_from_totals: boolean;
}

export interface TransactionCreate extends TransactionBase {
  account_id?: string | null;
  category_id?: string | null;
  split_type: SplitType;
}

export interface TransactionUpdate {
  category_id?: string | null;
  notes?: string | null;
  split_type?: SplitType | null;
  excluded_from_totals?: boolean | null;
  merchant_name?: string | null;
  account_id?: string | null;
  is_reimbursement?: boolean | null;
  reimbursement_for_transaction_id?: string | null;
  reimbursed_to_user_id?: string | null;
}

export interface ReimbursementMark {
  reimbursed_to_user_id: string;
  method?: SettlementMethod | null;
}

export interface ReimbursementAccept {
  linked_transaction_id: string;
  category_id: string;
  notification_id?: string;
}

export interface TransactionRead extends TransactionBase {
  id: string;
  account_id: string;
  user_id: string;
  category_id?: string | null;
  provider_transaction_id?: string | null;
  posted_at?: string | null;
  is_pending: boolean;
  is_expense: boolean;
  split_type: string;
  is_recurring: boolean;
  recurring_transaction_id?: string | null;
  is_reimbursement: boolean;
  reimbursement_for_transaction_id?: string | null;
  reimbursed_to_user_id?: string | null;
  linked_reimbursement_from_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface SplitCategoryInfo {
  category_name: string;
  category_icon?: string | null;
  category_color?: string | null;
  amount: Money;
}

export interface TransactionWithCategory extends TransactionRead {
  category_name?: string | null;
  category_icon?: string | null;
  category_color?: string | null;
  split_categories?: SplitCategoryInfo[] | null;
}

export interface UnsettledTransactionItem extends TransactionWithCategory {
  payer_name: string;
}

export interface TransactionSummary {
  id: string;
  description: string;
  amount: Money;
  transaction_type: string;
  date: string;
  category_name?: string | null;
  is_pending: boolean;
}

// =============================================================================
// Transaction Split Types
// =============================================================================

export interface TransactionSplitBase {
  category_id: string;
  amount: Money;
  split_type: SplitType;
  assigned_to_user_id?: string | null;
  notes?: string | null;
}

export interface TransactionSplitCreate extends TransactionSplitBase {
  transaction_id: string;
}

export interface TransactionSplitUpdate {
  category_id?: string | null;
  amount?: Money | null;
  notes?: string | null;
}

export interface TransactionSplitRead extends TransactionSplitBase {
  id: string;
  transaction_id: string;
  created_at: string;
}

export interface TransactionSplitWithCategory extends TransactionSplitRead {
  category_name: string;
  category_icon?: string | null;
  category_color?: string | null;
  assigned_to_user_name?: string | null;
  assigned_to_user_avatar_color?: string | null;
}

export interface TransactionSplitsUpdate {
  splits: TransactionSplitBase[];
}

// =============================================================================
// Expense Allocation Types
// =============================================================================

export interface ExpenseAllocationBase {
  user_id: string;
  amount: Money;
  percentage: string; // Decimal as string
  is_payer: boolean;
}

export interface ExpenseAllocationCreate extends ExpenseAllocationBase {
  transaction_id: string;
}

export interface ExpenseAllocationUpdate {
  amount?: Money | null;
  percentage?: string | null;
  is_payer?: boolean | null;
}

export interface ExpenseAllocationRead extends ExpenseAllocationBase {
  id: string;
  transaction_id: string;
  settlement_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ExpenseAllocationWithUser extends ExpenseAllocationRead {
  user_name: string;
  user_avatar_color?: string | null;
}

export interface ExpenseAllocationsUpdate {
  allocations: ExpenseAllocationBase[];
}

export interface UserBalance {
  user_id: string;
  user_name: string;
  total_paid: Money;
  total_responsible: Money;
  net_balance: Money;
}

// =============================================================================
// Budget Types
// =============================================================================

export interface BudgetBase {
  category_id: string;
  amount: Money;
  period_type: BudgetPeriod;
  start_date: string;
  end_date?: string | null;
  rollover: boolean;
  notify_at_percent: number;
}

export interface BudgetCreate extends BudgetBase {
  user_id?: string | null;
}

export interface BudgetUpdate {
  amount?: Money | null;
  end_date?: string | null;
  rollover?: boolean | null;
  notify_at_percent?: number | null;
}

export interface BudgetRead extends BudgetBase {
  id: string;
  household_id: string;
  user_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface BudgetWithProgressBasic extends BudgetRead {
  category_name: string;
  spent: Money;
  remaining: Money;
  percent_used: number;
  is_over_budget: boolean;
}

export interface BudgetSummary {
  id: string;
  category_name: string;
  amount: Money;
  spent: Money;
  percent_used: number;
}

// =============================================================================
// Category Rule Types
// =============================================================================

export interface CategoryRuleBase {
  pattern: string;
  match_type: MatchType;
  match_field: MatchField;
  case_sensitive: boolean;
  auto_split_type?: SplitType | null;
  priority: number;
  is_enabled: boolean;
}

export interface CategoryRuleCreate extends CategoryRuleBase {
  category_id: string;
}

export interface CategoryRuleUpdate {
  pattern?: string | null;
  match_type?: MatchType | null;
  match_field?: MatchField | null;
  case_sensitive?: boolean | null;
  category_id?: string | null;
  auto_split_type?: SplitType | null;
  priority?: number | null;
  is_enabled?: boolean | null;
}

export interface CategoryRuleRead extends CategoryRuleBase {
  id: string;
  household_id: string;
  category_id: string;
  created_at: string;
  updated_at: string;
}

export interface CategoryRuleWithCategory extends CategoryRuleRead {
  category_name: string;
}

export interface CategoryRuleTestRequest {
  pattern: string;
  match_type: MatchType;
  match_field: MatchField;
  case_sensitive: boolean;
  limit?: number;
}

export interface CategoryRuleTestMatch {
  id: string;
  description: string;
  merchant_name: string | null;
  amount: Money;
  date: string;
  transaction_type: string;
  matched_field: 'description' | 'merchant';
}

export interface CategoryRuleTestResponse {
  matches: CategoryRuleTestMatch[];
  total_tested: number;
  matched_count: number;
}

// =============================================================================
// Recurring Transaction Types
// =============================================================================

export interface RecurringTransactionBase {
  title: string;
  merchant_pattern?: string | null;
  expected_amount?: Money | null;
  amount_variance: Money;
  frequency: Frequency;
  day_of_month?: number | null;
  day_of_week?: number | null;
  transaction_type: TransactionType;
  split_type: SplitType;
  notes?: string | null;
  is_active: boolean;
}

export interface RecurringTransactionCreate extends RecurringTransactionBase {
  account_id?: string | null;
  category_id?: string | null;
  owner_user_id?: string | null;
  visibility_scope?: VisibilityScope | null;
}

export interface RecurringTransactionUpdate {
  title?: string | null;
  merchant_pattern?: string | null;
  expected_amount?: Money | null;
  amount_variance?: Money | null;
  frequency?: Frequency | null;
  day_of_month?: number | null;
  day_of_week?: number | null;
  account_id?: string | null;
  category_id?: string | null;
  split_type?: SplitType | null;
  notes?: string | null;
  is_active?: boolean | null;
  owner_user_id?: string | null;
  visibility_scope?: VisibilityScope | null;
}

export interface RecurringTransactionRead extends RecurringTransactionBase {
  id: string;
  household_id: string;
  account_id?: string | null;
  category_id?: string | null;
  is_auto_detected: boolean;
  last_occurred_at?: string | null;
  next_expected_at?: string | null;
  owner_user_id?: string | null;
  visibility_scope: VisibilityScope;
  created_at: string;
  updated_at: string;
  is_early_detection: boolean;
}

export interface RecurringTransactionWithCategory extends RecurringTransactionRead {
  category_name?: string | null;
  category_color?: string | null;
  category_icon?: string | null;
  account_name?: string | null;
  owner_name?: string | null;
  matched_count: number;
  missed_count: number;
  current_streak: number;
  anomaly_count: number;
}

export interface UpcomingTransaction {
  recurring_id: string;
  title: string;
  expected_date: string;
  expected_amount?: Money | null;
  category_name?: string | null;
  days_until: number;
}

export interface RecurringCandidate {
  merchant_pattern: string;
  expected_amount?: Money | null;
  amount_variance: Money;
  frequency: Frequency;
  transaction_type: TransactionType;
  transaction_count: number;
  confidence: number;
  is_early_detection: boolean;
  suggested_title: string;
  sample_transaction_ids: string[];
}

export interface RecurringCandidateConfirmItem {
  merchant_pattern: string;
  expected_amount?: Money | null;
  amount_variance: Money;
  frequency: Frequency;
  transaction_type: TransactionType;
  title: string;
  category_id?: string | null;
  account_id?: string | null;
  is_early_detection?: boolean;
  sample_transaction_ids?: string[];
}

// =============================================================================
// Recurring Cycle Types
// =============================================================================

export type CycleStatus = 'pending' | 'matched' | 'missed' | 'skipped';

export interface RecurringCycleSummary {
  id: string;
  recurring_transaction_id: string;
  cycle_number: number;
  expected_date: string;
  expected_amount: Money | null;
  cycle_status: CycleStatus;
  drift_days: number | null;
  amount_deviation: Money | null;
  amount_anomaly: boolean;
}

export interface RecurringCycleRead {
  id: string;
  recurring_transaction_id: string;
  household_id: string;
  cycle_number: number;
  expected_date: string;
  window_start: string;
  window_end: string;
  expected_amount: Money | null;
  cycle_status: CycleStatus;
  matched_transaction_id: string | null;
  matched_at: string | null;
  drift_days: number | null;
  amount_deviation: Money | null;
  skipped_at: string | null;
  skip_reason: string | null;
  missed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface RecurringCycleStats {
  total_cycles: number;
  matched_count: number;
  missed_count: number;
  skipped_count: number;
  current_streak: number;
  avg_drift_days: number | null;
  avg_amount_deviation: Money | null;
  anomaly_count: number;
  last_matched_date: string | null;
}

export interface MissedCycleCount {
  count: number;
}

// =============================================================================
// Settlement Types
// =============================================================================

export interface SettlementBase {
  from_user_id: string;
  to_user_id: string;
  amount: Money;
  settlement_date: string;
  settlement_type?: SettlementType;
  period_start?: string | null;
  period_end?: string | null;
  method?: SettlementMethod | null;
  notes?: string | null;
}

export interface SettlementCreate extends SettlementBase {
  allocation_ids?: string[] | null;
}

export interface SettlementUpdate {
  settlement_date?: string | null;
  method?: SettlementMethod | null;
  notes?: string | null;
}

export interface SettlementRead extends SettlementBase {
  id: string;
  household_id: string;
  balance_before?: Money | null;
  source_transaction_id?: string | null;
  created_at: string;
}

export interface SettlementWithUsers extends SettlementRead {
  from_user_name: string;
  to_user_name: string;
  from_user_avatar_color?: string | null;
  to_user_avatar_color?: string | null;
}

export interface CoveredCategory {
  category_name: string;
  count: number;
}

export interface SettlementDetail extends SettlementWithUsers {
  balance_after?: Money | null;
  covered_categories: CoveredCategory[];
}

export interface SettlementSummary {
  from_user_id: string;
  from_user_name: string;
  to_user_id: string;
  to_user_name: string;
  total_settled: Money;
  last_settlement_date?: string | null;
}

export interface FreshStartResult {
  settlements_created: number;
  settlements: SettlementRead[];
}

export interface SettlementPair {
  user_id: string;
  user_name: string;
  avatar_color: string | null;
  net_amount: Money;
  direction: 'they_owe' | 'you_owe';
  transaction_count: number;
}

export interface PairUnsettledTransaction {
  transaction_id: string;
  merchant_name: string | null;
  description: string | null;
  date: string;
  total_amount: Money;
  split_type: string;
  category_name: string | null;
  category_icon: string | null;
  category_color: string | null;
  payer_name: string;
  payer_user_id: string;
  bilateral_amount: Money;
  direction: 'they_owe' | 'you_owe';
  allocation_ids: string[];
  num_splits: number | null;
}

export interface PairUnsettledTransactionsResponse {
  items: PairUnsettledTransaction[];
  net_amount: Money;
  net_direction: 'they_owe' | 'you_owe';
}

// =============================================================================
// Receipt Types
// =============================================================================

export interface ReceiptBase {
  transaction_id: string;
}

export interface ReceiptCreate extends ReceiptBase {
  file_name: string;
}

export interface ReceiptRead {
  id: string;
  transaction_id: string;
  uploaded_by_user_id: string;
  file_path: string;
  file_name: string;
  file_size?: number | null;
  mime_type?: string | null;
  uploaded_at: string;
  download_url?: string | null;
}

export interface ReceiptWithUser extends ReceiptRead {
  uploaded_by_name: string;
}

export interface ReceiptUploadResponse {
  id: string;
  file_name: string;
  file_size: number;
  mime_type: string;
  message: string;
}

// =============================================================================
// Audit Log Types
// =============================================================================

export interface AuditLogRead {
  id: string;
  household_id: string;
  user_id?: string | null;
  entity_type: string;
  entity_id: string;
  action: AuditAction;
  changes?: Record<string, { old: unknown; new: unknown }> | null;
  context?: Record<string, unknown> | null;
  ip_address?: string | null;
  user_agent?: string | null;
  created_at: string;
}

export interface AuditLogWithUser extends AuditLogRead {
  user_name?: string | null;
}

export interface AuditLogSummary {
  id: string;
  entity_type: string;
  action: string;
  user_name?: string | null;
  created_at: string;
}

export interface AuditLogFilter {
  entity_type?: string | null;
  entity_id?: string | null;
  user_id?: string | null;
  action?: string | null;
  start_date?: string | null;
  end_date?: string | null;
}

// =============================================================================
// Bank Connection Types
// =============================================================================

export interface BankConnectionRead {
  id: string;
  user_id: string;
  provider: string;
  institution_id?: string | null;
  institution_name: string;
  institution_logo?: string | null;
  status: BankConnectionStatus;
  error_code?: string | null;
  error_message?: string | null;
  last_synced_at?: string | null;
  created_at: string;
  updated_at: string;
  accounts_count: number;
}

export interface BankConnectionSummary {
  id: string;
  institution_name: string;
  status: string;
  last_synced_at?: string | null;
}

export interface BankConnectionWithAccounts extends BankConnectionRead {
  accounts: AccountSummary[];
}

// =============================================================================
// Balance History Types
// =============================================================================

export interface BalanceHistoryRead {
  id: string;
  account_id: string;
  date: string;
  balance: Money;
  available_balance?: Money | null;
  source: BalanceSource;
  created_at: string;
}

export interface BalanceHistorySummary {
  date: string;
  balance: Money;
}

export interface AccountBalanceTrend {
  account_id: string;
  account_name: string;
  data_points: BalanceHistorySummary[];
  min_balance: Money;
  max_balance: Money;
  change: Money;
  change_percent?: number | null;
}

// =============================================================================
// UI Types (Frontend-only)
// =============================================================================

/**
 * Generic table column configuration.
 */
export interface TableColumn<T> {
  key: keyof T | string;
  header: string;
  sortable?: boolean;
  render?: (item: T) => React.ReactNode;
}

/**
 * Generic select option for dropdowns.
 */
export interface SelectOption<T> {
  value: string;
  label: string;
  model: T;
}

/**
 * Date range for filtering.
 */
export interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}

// =============================================================================
// Dashboard Types
// =============================================================================

export interface NetWorthAccount {
  id: string;
  name: string;
  type: string;
  balance: Money;
  source?: 'bank' | 'manual';
  asset_type?: string;
  is_liability?: boolean;
}

export interface NetWorthSummary {
  total_assets: Money;
  total_liabilities: Money;
  net_worth: Money;
  accounts: NetWorthAccount[];
}

export interface NetWorthBreakdownItem {
  category: string;
  value: Money;         // always positive (absolute); frontend uses is_liability for sign/color
  is_liability: boolean;
}

export interface SpendingByCategory {
  category_id: string | null; // null for uncategorized transactions
  category_name: string;
  parent_id: string | null;
  icon: string | null;
  color: string | null;
  total: Money;
  transaction_count: number;
}

export interface BalanceSummary {
  user_id: string;
  user_name: string;
  net_balance: Money;
}

export interface BudgetAlert {
  budget_id: string;
  category_id: string;
  category_name: string;
  category_icon: string | null;
  category_color: string | null;
  amount: Money;
  spent: Money;
  percent_used: number;
  status: 'ok' | 'warning' | 'over';
}

export interface RecentTransaction {
  id: string;
  date: string;
  merchant_name: string | null;
  description: string;
  amount: Money;
  transaction_type: TransactionType;
  split_type: SplitType;
  is_expense: boolean;
  category_name: string | null;
  category_icon: string | null;
}

export interface UpcomingBill {
  id: string;
  title: string;
  expected_amount: Money;
  next_expected_at: string;
  days_until: number;
  is_overdue: boolean;
}

export interface SetupProgress {
  has_connected_bank: boolean;
  has_categories: boolean;
  has_invited_members: boolean;
  has_budget: boolean;
}

export interface DashboardSummaryResponse {
  period_start: string;
  period_end: string;
  net_worth: NetWorthSummary;
  spending_by_category: SpendingByCategory[];
  total_spent: Money;
  total_income: Money;
  balance_summary: BalanceSummary | null;
  budget_alerts: BudgetAlert[];
  recent_transactions: RecentTransaction[];
  upcoming_bills: UpcomingBill[];
  unread_notification_count: number;
  net_worth_breakdown?: NetWorthBreakdownItem[];
  setup_progress: SetupProgress | null;
  daily_spending?: DailySpending[];
}

// =============================================================================
// Budget Progress Types (Enhanced)
// =============================================================================

export interface BudgetWithProgress extends BudgetRead {
  category_name: string;
  category_icon: string | null;
  category_color: string | null;
  current_period_start: string;
  current_period_end: string;
  spent: Money;
  remaining: Money;
  percent_used: number;
  is_over_budget: boolean;
  rollover_amount: Money;
  effective_budget: Money;
  is_at_threshold: boolean;
}

/** Category with high spending but no active budget. */
export interface BudgetSuggestion {
  category_id: string;
  category_name: string;
  category_icon: string | null;
  category_color: string | null;
  spent: Money;
}

/** One day's total spending within a budget period. */
export interface DailySpending {
  date: string;
  amount: Money;
}

/** Spending for a single subcategory within a budget. */
export interface SubcategorySpending {
  category_id: string;
  category_name: string;
  category_icon: string | null;
  category_color: string | null;
  amount: Money;
}

/** Analytics payload for the budget detail sheet. */
export interface BudgetAnalytics {
  daily_spending: DailySpending[];
  subcategory_spending: SubcategorySpending[];
  previous_period_spent: Money;
}

// =============================================================================
// User Preferences Types
// =============================================================================

export interface UserPreferencesRead {
  id: string;
  user_id: string;
  preferences: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface UserPreferencesUpdate {
  preferences: Record<string, unknown>;
}

// =============================================================================
// Sync Log Types
// =============================================================================

export type SyncStatus = 'success' | 'partial' | 'error';

export interface SyncLogRead {
  id: string;
  bank_connection_id: string;
  status: SyncStatus;
  transactions_added: number;
  transactions_updated: number;
  error_code?: string | null;
  error_message?: string | null;
  started_at: string;
  completed_at?: string | null;
  duration_ms?: number | null;
  created_at: string;
}

export interface SyncLogWithConnection extends SyncLogRead {
  institution_name: string;
}

// =============================================================================
// Tag Types
// =============================================================================

export interface TagCreate {
  name: string;
  color?: string | null;
}

export interface TagUpdate {
  name?: string | null;
  color?: string | null;
}

export interface TagRead {
  id: string;
  household_id: string;
  name: string;
  color?: string | null;
  created_at: string;
  updated_at: string;
}

export interface TagSummary {
  id: string;
  name: string;
  color?: string | null;
}

export interface TransactionTagsUpdate {
  tag_ids: string[];
}

// =============================================================================
// Activity Feed Types
// =============================================================================

export interface ShareDetail {
  user_id: string;
  user_name: string;
  amount: Money;
}

export interface ActivityExpenseItem {
  type: 'expense';
  id: string;
  date: string;
  payer_name: string;
  payer_user_id: string;
  amount: Money;
  merchant_name: string | null;
  description: string;
  split_type: string;
  category_name: string | null;
  category_icon: string | null;
  category_color: string | null;
  other_shares: ShareDetail[];
}

export interface ActivitySettlementItem {
  type: 'settlement';
  id: string;
  date: string;
  from_user_id: string;
  from_user_name: string;
  to_user_id: string;
  to_user_name: string;
  amount: Money;
  settlement_type: string;
  method: SettlementMethod | null;
  notes: string | null;
  source_transaction_id?: string | null;
}

export type ActivityItem = ActivityExpenseItem | ActivitySettlementItem;

export interface ActivityFeedResponse {
  items: ActivityItem[];
  total: number;
  page: number;
  page_size: number;
}

// =============================================================================
// Rollover Ledger Types
// =============================================================================

export type RolloverEntryType = 'open' | 'settled' | 'partial' | 'forgive';

export interface RolloverOtherUser {
  id: string;
  name: string;
  avatar_color: string | null;
}

export interface RolloverEntry {
  date: string;
  type: RolloverEntryType;
  label: string;
  other_user: RolloverOtherUser;
  amount: Money;
  rolled_over: Money | null;
  running_balance: Money | null;
  method: string | null;
  settlement_id: string | null;
  detail: string;
}

export interface RolloverLedgerResponse {
  items: RolloverEntry[];
}

// =============================================================================
// Banking Types
// =============================================================================

/**
 * Response from creating a link token (Teller or Plaid)
 */
export interface LinkTokenResponse {
  provider: string;
  // Teller fields
  application_id?: string;
  environment?: string;
  // Plaid fields
  link_token?: string;
}

/**
 * Account data from Teller enrollment
 */
export interface EnrollmentAccount {
  id: string;
  name: string;
  type: string;
  subtype: string;
  last_four: string;
}

/**
 * Request to exchange provider enrollment for stored connection
 */
export interface ExchangeRequest {
  provider?: string;
  // Teller fields
  access_token?: string;
  enrollment_id?: string;
  institution: {
    id: string;
    name: string;
  };
  accounts?: EnrollmentAccount[];
  // Plaid fields
  public_token?: string;
  products?: string[];
}

/**
 * Response from exchanging Teller enrollment
 */
export interface ExchangeResponse {
  connection_id: string;
  institution_name: string;
  accounts_linked: number;
}

/**
 * Per-account sync result detail
 */
export interface SyncAccountResult {
  account_id: string;
  account_name: string;
  transactions_added: number;
  transactions_updated: number;
}

/**
 * Response from syncing a bank connection
 */
export interface SyncResponse {
  success: boolean;
  connection_id: string;
  accounts_synced: number;
  transactions: {
    added: number;
    updated: number;
  };
  synced_at: string;
  errors: string[];
  account_results: SyncAccountResult[];
  holdings_refreshed: boolean;
}

/**
 * Bank connection record
 */
export interface BankConnection {
  id: string;
  user_id: string;
  provider: string;
  institution_name: string;
  institution_id: string;
  institution_logo: string | null;
  status: BankConnectionStatus;
  last_synced_at: string | null;
  accounts_count: number;
  error_code?: string;
  error_message?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Account record (banking feature view — simpler shape returned by banking endpoints).
 * See `AccountRead` above for the full account schema from /accounts endpoints.
 */
export interface Account {
  id: string;
  name: string;
  type: string;
  subtype: string;
  mask: string | null;
  current_balance: Money | null;
  available_balance: Money | null;
  credit_limit: Money | null;
  is_hidden: boolean;
  is_manual: boolean;
  is_system_account: boolean;
  bank_connection_id: string | null;
  institution_name: string | null;
  user_id?: string;
}

// =============================================================================
// Investment Holdings Types
// =============================================================================

export interface InvestmentHoldingRead {
  id: string;
  account_id: string;
  ticker: string | null;
  name: string;
  quantity: string;
  value: Money;
  cost_basis: Money | null;
  holding_type: string;
  provider_security_id: string | null;
  currency: string;
  gain_loss: Money | null;
  gain_loss_percent: string | null;
  created_at: string;
  updated_at: string;
}

// =============================================================================
// Goal Types
// =============================================================================

export type GoalType = 'savings' | 'custom';

export interface GoalRead {
  id: string;
  household_id: string;
  user_id: string | null;
  name: string;
  goal_type: GoalType;
  target_amount: Money;
  current_amount: Money;
  target_date: string | null;
  icon: string | null;
  color: string | null;
  priority: number;
  /** @deprecated Use account_links on GoalWithProgress instead. */
  linked_account_id: string | null;
  monthly_contribution: Money | null;
  metadata: Record<string, unknown> | null;
  is_active: boolean;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface GoalWithProgress extends GoalRead {
  percent_complete: number;
  remaining_amount: Money;
  is_on_track: boolean | null;
  projected_completion_date: string | null;
  days_remaining: number | null;
  milestones?: GoalMilestoneRead[];
  account_links: GoalAccountLinkRead[];
  contributions: GoalContributionRead[];
  contribution_breakdown: ContributionBreakdown[];
}

export interface GoalSummary {
  id: string;
  name: string;
  goal_type: string;
  user_id: string | null;
  icon: string | null;
  color: string | null;
  target_amount: Money;
  current_amount: Money;
  percent_complete: number;
  target_date: string | null;
  is_on_track: boolean | null;
}

export interface GoalMilestoneRead {
  id: string;
  goal_id: string;
  title: string;
  target_amount: Money;
  reached_at: string | null;
  notified_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface GoalCreate {
  name: string;
  goal_type: GoalType;
  target_amount: Money;
  target_date?: string | null;
  icon?: string | null;
  color?: string | null;
  priority?: number;
  linked_account_id?: string | null;
  monthly_contribution?: Money | null;
  metadata?: Record<string, unknown> | null;
  user_id?: string | null;
  initial_account_links?: GoalAccountLinkCreate[];
}

export interface GoalUpdate {
  name?: string | null;
  target_amount?: Money | null;
  current_amount?: Money | null;
  target_date?: string | null;
  icon?: string | null;
  color?: string | null;
  priority?: number | null;
  linked_account_id?: string | null;
  monthly_contribution?: Money | null;
  is_active?: boolean | null;
  metadata?: Record<string, unknown> | null;
}

export interface GoalMilestoneCreate {
  title: string;
  target_amount: Money;
}

export interface GoalProgressUpdate {
  current_amount: Money;
}

export interface GoalAccountLinkRead {
  id: string;
  goal_id: string;
  account_id: string;
  user_id: string;
  allocated_amount: Money | null;
  label: string | null;
  is_active: boolean;
  account_name: string | null;
  user_display_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface GoalAccountLinkCreate {
  account_id: string;
  allocated_amount?: Money | null;
  label?: string | null;
}

export interface GoalAccountLinkResponse {
  link: GoalAccountLinkRead;
  warnings: string[] | null;
}

export interface GoalContributionRead {
  id: string;
  goal_id: string;
  user_id: string;
  amount: Money;
  note: string | null;
  contributed_at: string;
  user_display_name: string | null;
  created_at: string;
  deleted_at: string | null;
}

export interface GoalContributionCreate {
  amount: Money;
  note?: string | null;
  contributed_at?: string | null;
}

export interface ContributionBreakdown {
  user_id: string;
  user_display_name: string | null;
  linked_amount: Money;
  manual_amount: Money;
  total: Money;
}

// =============================================================================
// Manual Asset Types
// =============================================================================

export type ManualAssetType = 'real_estate' | 'vehicle' | 'crypto' | 'jewelry' | 'collectible' | 'other';

export interface ManualAssetRead {
  id: string;
  household_id: string;
  user_id: string | null;
  name: string;
  asset_type: ManualAssetType;
  is_liability: boolean;
  current_value: Money;
  currency: string;
  notes: string | null;
  last_valued_at: string;
  created_at: string;
  updated_at: string;
}

export interface ManualAssetCreate {
  user_id?: string | null;
  name: string;
  asset_type: ManualAssetType;
  is_liability?: boolean;
  current_value: Money;
  currency?: string;
  notes?: string | null;
  last_valued_at?: string | null;
}

export interface ManualAssetUpdate {
  name?: string | null;
  asset_type?: ManualAssetType | null;
  is_liability?: boolean | null;
  current_value?: Money | null;
  currency?: string | null;
  notes?: string | null;
  last_valued_at?: string | null;
}

export interface ManualAssetSummary {
  id: string;
  user_id: string | null;
  name: string;
  asset_type: ManualAssetType;
  is_liability: boolean;
  current_value: Money;
  currency: string;
}

export interface ManualAssetHistoryRead {
  id: string;
  manual_asset_id: string;
  value: Money;
  recorded_at: string;
}

/** Display metadata for each asset type: label + lucide icon name. */
export const MANUAL_ASSET_TYPE_META: Record<ManualAssetType, { label: string; icon: string }> = {
  real_estate: { label: 'Real Estate', icon: 'home' },
  vehicle: { label: 'Vehicle', icon: 'car' },
  crypto: { label: 'Cryptocurrency', icon: 'bitcoin' },
  jewelry: { label: 'Jewelry', icon: 'gem' },
  collectible: { label: 'Collectible', icon: 'trophy' },
  other: { label: 'Other', icon: 'package' },
};

// =============================================================================
// Notification Types
// =============================================================================

export type NotificationType = 'goal_milestone' | 'budget_alert' | 'settlement_reminder' | 'system' | 'reimbursement_request' | 'net_worth_share_request';
export type NotificationSeverity = 'info' | 'success' | 'warning' | 'critical';

export interface NotificationRead {
  id: string;
  household_id: string;
  user_id: string;
  notification_type: NotificationType;
  title: string;
  body: string;
  icon: string | null;
  severity: NotificationSeverity;
  action_url: string | null;
  action_label: string | null;
  is_read: boolean;
  read_at: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export interface NotificationListParams {
  is_read?: boolean;
  notification_type?: NotificationType;
  page?: number;
  page_size?: number;
}

export interface UnreadCountResponse {
  count: number;
}

// ── AI / Chat ──────────────────────────────────

export interface AIConversation {
  id: string;
  household_id: string;
  user_id: string;
  title: string | null;
  created_at: string;
  updated_at: string;
}

export interface AIConversationList extends AIConversation {
  message_count: number;
}

export interface AIMessage {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant' | 'tool';
  content: string;
  tool_calls: Record<string, unknown> | null;
  feedback: -1 | 0 | 1 | null;
  created_at: string;
}

export interface AIConversationDetail extends AIConversation {
  messages: AIMessage[];
}

export type AIStreamEvent =
  | { type: 'text_delta'; content: string }
  | { type: 'tool_call'; name: string; result_summary: string }
  | { type: 'done' }
  | { type: 'error'; message: string };

export interface AIInsightResponse {
  content: string;
  page: string;
  cached: boolean;
  generated_at: string;
  tools_used: string[];
}

export interface AITransactionInsightResponse {
  transaction_id: string;
  content: string;
  cached: boolean;
  generated_at: string;
}

// =============================================================================
// Report Types
// =============================================================================

export interface ReportParams {
  period_start: string;   // ISO date string YYYY-MM-DD
  period_end: string;
  user_id?: string;
  household?: string;     // "true" for household mode
  compare_start?: string;
  compare_end?: string;
}

export interface CategorySpendingEntry {
  category_id: string;
  category_name: string;
  category_icon: string | null;
  category_color: string | null;
  total: Money;
  count: number;
  percentage: number;
}

export interface SpendingOverTimeEntry {
  period_start: string;
  period_end: string;
  label: string;
  total_spent: Money;
  total_income: Money;
  categories: CategorySpendingEntry[];
}

export interface SpendingOverTimeResponse {
  periods: SpendingOverTimeEntry[];
  grand_total_spent: Money;
  grand_total_income: Money;
}

export interface IncomeVsExpenseEntry {
  period_start: string;
  period_end: string;
  label: string;
  income: Money;
  expenses: Money;
  net_savings: Money;
}

export interface IncomeVsExpenseResponse {
  periods: IncomeVsExpenseEntry[];
  total_income: Money;
  total_expenses: Money;
  total_net_savings: Money;
}

export interface CategoryDrillDownResponse {
  categories: CategorySpendingEntry[];
  total_spent: Money;
  total_income: Money;
  period_start: string;
  period_end: string;
}

export interface NetWorthTrendEntry {
  date: string;
  total_assets: Money;
  total_liabilities: Money;
  net_worth: Money;
}

export interface NetWorthTrendResponse {
  snapshots: NetWorthTrendEntry[];
  current_net_worth: Money;
  change_amount: Money;
  change_percent: number;
}

export interface BudgetVsActualEntry {
  budget_id: string;
  category_name: string;
  category_icon: string | null;
  category_color: string | null;
  native_budget: Money;
  budgeted: Money;
  actual: Money;
  remaining: Money;
  percent_used: number;
  is_over: boolean;
  period_type: string;
}

export interface BudgetVsActualResponse {
  budgets: BudgetVsActualEntry[];
  total_budgeted: Money;
  total_actual: Money;
  period_start: string;
  period_end: string;
}

export * from './preferences';
